// src/components/HIPAA.js
import React, { useState } from 'react';
import './HIPAA.css';

const HIPAA = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    practiceName: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://formspree.io/f/xrejwqgl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Thank you! We will contact you shortly about our HIPAA-compliant services.' });
        setFormData({ name: '', email: '', phone: '', practiceName: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again or call us directly.' });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus({ type: '', message: '' }), 5000);
    }
  };

  const complianceFeatures = [
    {
      title: 'STRICT DATA PRIVACY PROTOCOLS',
      description: 'We ensure that PHI is only accessed by authorized personnel and solely for permitted purposes such as medical billing, claim submission, or payment reconciliation. Our staff members follow the Minimum Necessary Standard, accessing only the information needed to complete their tasks.'
    },
    {
      title: 'SECURE ELECTRONIC SYSTEMS',
      description: 'We use HIPAA-compliant billing software and secure cloud-based platforms with features like:',
      list: ['End-to-end data encryption', 'Multi-factor authentication', 'Secure backups', 'Audit logs for monitoring data access'],
      note: 'All electronic PHI (ePHI) is stored and transmitted using secure channels, in line with the HIPAA Security Rule.'
    },
    {
      title: 'BUSINESS ASSOCIATE AGREEMENTS (BAAS)',
      description: 'We enter into BAAs with all covered entities (our healthcare clients) and technology partners to formally define how PHI is protected. This ensures accountability and shared responsibility for HIPAA compliance.'
    },
    {
      title: 'ONGOING STAFF TRAINING',
      description: 'Every employee at Providers Care Billing LLC undergoes regular HIPAA training, which covers:',
      list: ['Data privacy and confidentiality', 'Proper handling of PHI', 'Recognizing and reporting breaches', 'Best practices for electronic security'],
      note: 'This training is refreshed annually and when regulations or procedures change.'
    },
    {
      title: 'RISK ASSESSMENTS AND CONTINUOUS IMPROVEMENT',
      description: 'We regularly perform internal risk assessments to identify potential vulnerabilities and update our safeguards accordingly. Our security protocols evolve with emerging threats and changing technologies, ensuring we stay ahead of potential risks.'
    },
    {
      title: 'BREACH PREVENTION AND RESPONSE PLAN',
      description: 'While we work diligently to prevent breaches, we also maintain a clear incident response protocol. In the rare event of a breach, we are prepared to act swiftly—informing our clients and taking corrective action, as required by the HIPAA Breach Notification Rule.'
    }
  ];

  return (
    <main className="hipaa-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="container">
          <span className="breadcrumb-item">Home</span>
          <span className="breadcrumb-separator">→</span>
          <span className="breadcrumb-item active">HIPAA Compliance</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hipaa-hero">
        <div className="container">
          <div className="hipaa-hero-content">
            <h1 className="hipaa-hero-title">HIPAA Compliance</h1>
            <div className="hero-tagline">
              <p>HIPAA compliance is not a checkbox — <span className="highlight">it's a promise:</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="hipaa-intro">
        <div className="container">
          <div className="intro-card">
            <h2 className="intro-title">HOW PROVIDERS CARE BILLING LLC PRIORITIZES HIPAA COMPLIANCE?</h2>
            <p className="intro-text">
              At Providers Care Billing LLC, we understand that patient data is more than just information — it represents a 
              person's privacy, dignity, and trust. As a medical billing company handling sensitive health information daily, 
              we take our responsibility under the Health Insurance Portability and Accountability Act (HIPAA) very seriously.
            </p>
            <p className="intro-text">
              HIPAA compliance is not just a legal requirement for us — it's a core part of our culture and commitment to 
              healthcare providers and their patients.
            </p>
          </div>
        </div>
      </section>

      {/* Understanding Role Section */}
      <section className="hipaa-role">
        <div className="container">
          <div className="role-content">
            <h2 className="role-title">UNDERSTANDING OUR ROLE UNDER HIPAA</h2>
            <p className="role-text">
              As a business associate under HIPAA, we are entrusted with protected health information (PHI) by our healthcare 
              clients. This includes handling medical records, billing details, insurance claims, and patient identifiers. 
              To uphold this trust, we have implemented a comprehensive HIPAA compliance program that safeguards data at every 
              step—from intake to final payment.
            </p>
          </div>
        </div>
      </section>

      {/* How We Follow Section */}
      <section className="hipaa-compliance">
        <div className="container">
          <h2 className="compliance-main-title">How We Follow HIPAA Rules at Providers Care Billing LLC?</h2>
          
          <div className="compliance-grid">
            {complianceFeatures.map((feature, index) => (
              <div className="compliance-card" key={index}>
                <div className="card-number">{index + 1}</div>
                <h3 className="card-title">{feature.title}</h3>
                <p className="card-description">{feature.description}</p>
                {feature.list && (
                  <ul className="card-list">
                    {feature.list.map((item, idx) => (
                      <li key={idx}>
                        <span className="list-icon">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {feature.note && (
                  <p className="card-note">{feature.note}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transparency Section */}
      <section className="hipaa-transparency">
        <div className="container">
          <div className="transparency-content">
            <h2 className="transparency-title">BUILDING TRUST THROUGH TRANSPARENCY</h2>
            <p className="transparency-text">
              We believe that transparency is key to building lasting relationships with our healthcare clients. By keeping 
              open lines of communication and providing regular compliance updates, we reinforce our commitments to HIPAA 
              and to the protection of patient data.
            </p>
          </div>
        </div>
      </section>

      {/* Conclusion Section */}
      <section className="hipaa-conclusion">
        <div className="container">
          <div className="conclusion-content">
            <h2 className="conclusion-title">Conclusion</h2>
            <p className="conclusion-text">
              At Providers Care Billing LLC, HIPAA compliance is not a checkbox — it's a promise. A promise to protect the 
              sensitive information entrusted to us. A promise to support our healthcare partners with integrity. And above all, 
              a promise to treat patient data with the same care and respect as we would our own.
            </p>
            <p className="conclusion-text">
              If you're a healthcare provider looking for a billing partner who puts security and compliance first, we'd be 
              honored to support your practice.
            </p>
            <button 
              className="contact-hipaa-btn" 
              onClick={() => document.getElementById('hipaa-contact-form').scrollIntoView({ behavior: 'smooth' })}
            >
              Contact us today to learn more about our secure and HIPAA-compliant billing service.
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="hipaa-contact" id="hipaa-contact-form">
        <div className="container">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2 className="contact-section-title">HIPAA CONTACTS AT PROVIDERS CARE BILLING LLC:</h2>
              <p className="contact-disclaimer">
                Have a question or see something unusual? Speak up — protecting privacy starts with you. Compliance Dept.
              </p>
              <div className="contact-details">
                <div className="contact-item">
                  <span className="contact-icon">📞</span>
                  <span>Phone: <strong>888-495-3786</strong></span>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">✉️</span>
                  <span>Email: <strong>info@providerscarebilling.com</strong></span>
                </div>
              </div>
            </div>
            <div className="contact-form">
              <h3>Send us a message</h3>
              {submitStatus.message && (
                <div className={`form-status ${submitStatus.type}`}>
                  {submitStatus.message}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <input type="text" name="name" placeholder="Your Name" value={formData.name} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <input type="email" name="email" placeholder="Your Email" value={formData.email} onChange={handleChange} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <input type="text" name="practiceName" placeholder="Practice Name" value={formData.practiceName} onChange={handleChange} />
                  </div>
                </div>
                <div className="form-group">
                  <textarea name="message" rows="4" placeholder="Your Message" value={formData.message} onChange={handleChange} required></textarea>
                </div>
                <button type="submit" className="submit-hipaa-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message →'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HIPAA;